import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

/** Cart attribute that remembers the shopper took the gift out on purpose. */
const DECLINED = '_look_gift_declined';

/**
 * Stops a gift that keeps failing to add (sold out, removed in the admin) from
 * turning every cart render into another request. Once per page is plenty: the
 * next page load tries again.
 */
let autoAddTried = false;

/**
 * The welcome gift, as a row above the cart totals.
 *
 * The gift has its real price, and the welcome code takes 100% off it and nothing
 * else, so every other line keeps its own discount. A Buy X Get Y code could not do
 * it: the piece counted as the "buy" item loses its own discount. The code alone
 * never puts the gift in the cart and does not stop one going through on its own
 * (it applies to the gift whatever else is there), so this element is what keeps
 * the cart honest, and it gives the shopper the one control she needs: which
 * style, or none at all. The rules, re-checked against a freshly read cart every
 * time the render suggests something is off (see `shipping-protection.js` for why
 * the rendered attributes are never trusted on their own):
 *
 * - a gift never sits in a cart without the code and another piece
 * - at most one gift per cart
 * - an eligible cart gets the gift on its own, unless she has declined it
 *
 * Every mutation goes through `/cart/update.js`, which can swap the style, remove
 * the line and record the decline in a single request.
 *
 * @extends {Component}
 */
class LookGiftComponent extends Component {
  #busy = false;

  static observedAttributes = ['data-state', 'data-quantity', 'data-lines', 'data-declined'];

  connectedCallback() {
    super.connectedCallback();
    this.#enforce();
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#enforce();
  }

  /** @param {any} cart */
  #giftLines(cart) {
    const productId = this.dataset.productId;
    return (cart.items || []).filter((item) =>
      productId ? String(item.product_id) === productId : item.product_type === 'Gift'
    );
  }

  /** @param {any} cart */
  #eligible(cart) {
    const code = (this.dataset.code || '').toUpperCase();
    if (!code) return false;
    const hasCode = (cart.discount_codes || []).some(
      (/** @type {{ code: string }} */ discount) => discount.code.toUpperCase() === code
    );
    return (
      hasCode &&
      (cart.items || []).some((item) => item.product_type !== 'Gift' && item.handle !== 'shipping-protection')
    );
  }

  async #readCart() {
    return fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => response.json());
  }

  async #enforce() {
    if (this.#busy) return;

    const { state } = this.dataset;
    // `absent` always needs the cart: Liquid cannot tell whether the code is on.
    const suspect =
      state === 'orphan' || state === 'absent' || Number(this.dataset.quantity) > 1 || Number(this.dataset.lines) > 1;
    if (!suspect) return;

    const cart = await this.#readCart();
    if (this.#busy) return;

    const lines = this.#giftLines(cart);
    const eligible = this.#eligible(cart);
    if (state === 'absent') this.hidden = !eligible;

    if (!eligible) {
      if (lines.length === 0) return;
      /** @type {Record<string, number>} */
      const updates = {};
      for (const line of lines) updates[line.key] = 0;
      this.#update({ updates }, 'remove');
      return;
    }

    if (lines.length === 0) {
      if (cart.attributes?.[DECLINED] === '1' || autoAddTried) return;
      const variantId = this.dataset.defaultVariantId;
      if (!variantId) return;
      autoAddTried = true;
      this.#update({ updates: { [variantId]: 1 } }, 'add');
      return;
    }

    const [first, ...rest] = lines;
    if (first.quantity > 1 || rest.length > 0) {
      /** @type {Record<string, number>} */
      const updates = { [first.key]: 1 };
      for (const line of rest) updates[line.key] = 0;
      this.#update({ updates }, 'update');
    }
  }

  /** @param {Event} event */
  changeStyle = async (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || this.#busy) return;

    const cart = await this.#readCart();
    const lines = this.#giftLines(cart);
    const variantId = select.value;
    if (lines.length === 1 && String(lines[0].variant_id) === variantId) return;

    /** @type {Record<string, number>} */
    const updates = {};
    for (const line of lines) updates[line.key] = 0;

    if (lines.length === 0) {
      // Not in the cart any more (another tab took it out). Picking a style is still
      // a clear yes, so it goes in, and the decline is cleared with it.
      this.#update({ updates: { [variantId]: 1 }, attributes: { [DECLINED]: '' } }, 'add');
      return;
    }

    // Keys and variant ids can share one `updates` object: the zeros take the old line
    // out by key, and the variant id puts the new style in.
    updates[variantId] = 1;
    this.#update({ updates }, 'update');
  };

  remove = async () => {
    if (this.#busy) return;
    const cart = await this.#readCart();

    /** @type {Record<string, number>} */
    const updates = {};
    for (const line of this.#giftLines(cart)) updates[line.key] = 0;

    this.#update({ updates, attributes: { [DECLINED]: '1' } }, 'remove');
  };

  add = async () => {
    if (this.#busy) return;
    const select = this.querySelector('select');
    const variantId = select instanceof HTMLSelectElement ? select.value : this.dataset.defaultVariantId;
    if (!variantId) return;

    const cart = await this.#readCart();
    if (this.#giftLines(cart).length > 0) return this.#update({ attributes: { [DECLINED]: '' } }, 'update');

    this.#update({ updates: { [variantId]: 1 }, attributes: { [DECLINED]: '' } }, 'add');
  };

  /**
   * Posts to `/cart/update.js` and hands the re-rendered sections to every cart
   * component, the same way the theme's own cart mutations do.
   *
   * @param {{ updates?: Record<string, number>, attributes?: Record<string, string> }} body
   * @param {'add' | 'remove' | 'update'} action
   */
  #update(body, action) {
    this.#busy = true;
    this.classList.add('look-gift-cart--busy');

    const sections = new Set();
    for (const component of document.querySelectorAll('cart-items-component')) {
      if (component instanceof HTMLElement && component.dataset.sectionId) sections.add(component.dataset.sectionId);
    }

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines: [], promise: deferred.promise }));

    fetch(
      Theme.routes.cart_update_url,
      fetchConfig('json', {
        body: JSON.stringify({ ...body, sections: [...sections].join(','), sections_url: window.location.pathname }),
      })
    )
      .then((response) => response.json())
      .then((cart) => {
        if (cart.status) throw new Error(cart.description || cart.message || 'Gift update failed');

        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { source: 'look-gift-component', items: cart.items, sections: cart.sections, didError: false },
        });
      })
      .catch((error) => {
        deferred.reject(error);
        this.dispatchEvent(
          new CartErrorEvent({ error: error?.message || 'Gift update failed', code: 'SERVICE_UNAVAILABLE' })
        );
      })
      .finally(() => {
        this.#busy = false;
        this.classList.remove('look-gift-cart--busy');
      });
  }
}

if (!customElements.get('look-gift-component')) {
  customElements.define('look-gift-component', LookGiftComponent);
}
